import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from "@aws-sdk/client-cognito-identity";
import { LambdaClient, InvokeCommand } from "@aws-sdk/client-lambda";
import { config } from "../config";
import { AuthTokens, TemporaryCredentials } from "../types";

const COGNITO_PROVIDER = () => `cognito-idp.${config.region}.amazonaws.com/${config.cognitoUserPoolId}`;

/**
 * Cognito auth + Lambda-mediated catalog access.
 *
 * Flow:
 * 1. Cognito hosted UI (Entra ID SAML) → ID token + access token
 * 2. Identity Pool → Cognito credentials (can only invoke Lambda)
 * 3. Lambda handles all AWS operations, validates token, returns data/credentials
 */
export class AuthModule {
  private tokens: AuthTokens | null = null;
  private cognitoCredentials: TemporaryCredentials | null = null;
  private projectCredentials: TemporaryCredentials | null = null;
  private _sessionId: string | null = null;
  private _onTokenRefresh: (() => void) | null = null;

  get sessionId(): string | null { return this._sessionId; }
  set onTokenRefresh(cb: (() => void) | null) { this._onTokenRefresh = cb; }

  /** Restore tokens from sessionStorage (set by login page). */
  async restore(): Promise<boolean> {
    const stored = sessionStorage.getItem("xlai-tokens");
    if (!stored) return false;
    try {
      const tokens = JSON.parse(stored);
      if (tokens.expiresAt <= Date.now()) return false;
      this.tokens = tokens;
      this._sessionId = `ses-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await this.getCognitoCredentials();
      return true;
    } catch {
      return false;
    }
  }

  /** Sign in via Cognito hosted UI. */
  async signIn(): Promise<AuthTokens> {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const state = crypto.randomUUID();

    const authUrl =
      `https://${config.cognitoDomain}/oauth2/authorize?` +
      `response_type=code&client_id=${config.cognitoClientId}` +
      `&redirect_uri=${encodeURIComponent(config.appDomain + "/oauth2/idpresponse")}` +
      `&scope=openid+email&state=${state}` +
      `&code_challenge=${codeChallenge}&code_challenge_method=S256` +
      `&identity_provider=EntraID`;

    const code = await this.openDialogAndGetCode(authUrl, state);
    this.tokens = await this.exchangeCodeForTokens(code, codeVerifier);
    this._sessionId = `ses-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    await this.getCognitoCredentials();
    return this.tokens;
  }

  /** Get Cognito Identity Pool credentials (can only invoke Lambda). */
  private async getCognitoCredentials(): Promise<TemporaryCredentials> {
    // Use a 60-second buffer to avoid returning credentials that expire mid-request
    const expiryBuffer = new Date(Date.now() + 60_000);
    if (this.cognitoCredentials && this.cognitoCredentials.expiration > expiryBuffer) {
      return this.cognitoCredentials;
    }
    if (!this.tokens) throw new Error("Not authenticated.");

    const client = new CognitoIdentityClient({ region: config.region });
    const idResp = await client.send(
      new GetIdCommand({
        IdentityPoolId: config.cognitoIdentityPoolId,
        Logins: { [COGNITO_PROVIDER()]: this.tokens.idToken },
      })
    );
    const credsResp = await client.send(
      new GetCredentialsForIdentityCommand({
        IdentityId: idResp.IdentityId!,
        Logins: { [COGNITO_PROVIDER()]: this.tokens.idToken },
      })
    );
    const c = credsResp.Credentials!;
    this.cognitoCredentials = {
      accessKeyId: c.AccessKeyId!,
      secretAccessKey: c.SecretKey!,
      sessionToken: c.SessionToken!,
      expiration: c.Expiration!,
    };
    return this.cognitoCredentials;
  }

  /** Invoke the catalog Lambda (or dev server endpoint). */
  async invokeCatalog(action: string, params: Record<string, string> = {}): Promise<unknown> {
    if (!this.tokens) throw new Error("Not authenticated.");
    await this.ensureTokenValid();

    const requestId = `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const body = JSON.stringify({ action, params, idToken: this.tokens.idToken, requestId, sessionId: this._sessionId });

    // Dev mode: call local endpoint — restrict to same-origin to prevent idToken exfiltration
    // via a compromised or tampered app-config.json.
    if (config.catalogApiUrl) {
      const targetOrigin = new URL(config.catalogApiUrl, window.location.origin).origin;
      if (targetOrigin !== window.location.origin) {
        throw new Error(`catalogApiUrl must be same-origin (got ${targetOrigin})`);
      }
      const resp = await fetch(config.catalogApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Request failed" }));
        const msg = err.error || `Catalog request failed: ${resp.status}`;
        throw Object.assign(new Error(msg), { requestId: err.requestId || requestId });
      }
      return resp.json();
    }

    // Prod mode: invoke Lambda directly
    const creds = await this.getCognitoCredentials();
    const lambda = new LambdaClient({
      region: config.region,
      credentials: {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
      },
    });
    const resp = await lambda.send(
      new InvokeCommand({
        FunctionName: config.catalogLambdaName,
        Payload: new TextEncoder().encode(JSON.stringify({ body })),
      })
    );
    const result = JSON.parse(new TextDecoder().decode(resp.Payload!));
    if (result.statusCode !== 200) {
      const parsed = JSON.parse(result.body);
      throw Object.assign(new Error(parsed.error), { requestId: parsed.requestId || requestId });
    }
    return JSON.parse(result.body);
  }

  /** Get project credentials via Lambda (role ARN never exposed). */
  private lastProjectName: string | null = null;

  async getProjectCredentials(projectName: string): Promise<TemporaryCredentials> {
    // Use a 60-second buffer to avoid returning credentials that expire mid-query
    const expiryBuffer = new Date(Date.now() + 60_000);
    if (this.projectCredentials && this.lastProjectName === projectName && this.projectCredentials.expiration > expiryBuffer) {
      return this.projectCredentials;
    }
    const result = await this.invokeCatalog("assumeProjectRole", { projectName }) as {
      accessKeyId: string; secretAccessKey: string; sessionToken: string; expiration: string;
    };
    this.lastProjectName = projectName;
    this.projectCredentials = {
      accessKeyId: result.accessKeyId,
      secretAccessKey: result.secretAccessKey,
      sessionToken: result.sessionToken,
      expiration: new Date(result.expiration),
    };
    return this.projectCredentials;
  }

  clearProjectCredentials(): void { this.projectCredentials = null; }

  signOut(): void {
    this.tokens = null;
    this.cognitoCredentials = null;
    this.projectCredentials = null;
    this._sessionId = null;
  }

  isAuthenticated(): boolean { return this.tokens !== null; }
  getTokens(): AuthTokens | null { return this.tokens; }

  // ─── Token exchange ───────────────────────────────────────────────────────

  private async exchangeCodeForTokens(code: string, codeVerifier: string): Promise<AuthTokens> {
    const resp = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        client_id: config.cognitoClientId,
        code,
        redirect_uri: `${config.appDomain}/oauth2/idpresponse`,
        code_verifier: codeVerifier,
      }),
    });
    if (!resp.ok) throw new Error("Failed to exchange auth code for tokens.");
    const d = await resp.json();
    return {
      accessToken: d.access_token,
      idToken: d.id_token,
      refreshToken: d.refresh_token,
      expiresAt: Date.now() + d.expires_in * 1000,
    };
  }

  private async ensureTokenValid(): Promise<void> {
    if (!this.tokens) throw new Error("Not authenticated.");
    if (Date.now() < this.tokens.expiresAt - 300_000) return; // 5 min buffer
    // Refresh via Cognito (CORS supported)
    const resp = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: config.cognitoClientId,
        refresh_token: this.tokens.refreshToken,
      }),
    });
    if (!resp.ok) throw new Error("Session expired. Please sign in again.");
    const d = await resp.json();
    this.tokens = {
      ...this.tokens,
      accessToken: d.access_token,
      idToken: d.id_token,
      expiresAt: Date.now() + d.expires_in * 1000,
    };
    this.cognitoCredentials = null; // Force refresh
    if (this._onTokenRefresh) this._onTokenRefresh();
  }

  // ─── Dialog / popup ───────────────────────────────────────────────────────

  private openDialogAndGetCode(url: string, expectedState: string): Promise<string> {
    if (typeof Office === "undefined" || !Office?.context?.host) return this.openPopupAndGetCode(url, expectedState);

    const dialogUrl = `${config.appDomain}/oauth2/redirect.html#${encodeURIComponent(url)}`;
    return new Promise((resolve, reject) => {
      Office.context.ui.displayDialogAsync(dialogUrl, { height: 60, width: 40 }, (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(`Failed to open sign-in dialog: ${result.error?.message || "unknown"}`));
          return;
        }
        const dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: unknown) => {
          dialog.close();
          const msgArg = arg as { message?: string };
          if (!msgArg.message) { reject(new Error("Sign-in failed.")); return; }
          const params = new URLSearchParams(msgArg.message);
          const code = params.get("code");
          const state = params.get("state");
          if (state !== expectedState) { reject(new Error("Invalid auth state.")); return; }
          if (!code) { reject(new Error("No auth code received.")); return; }
          resolve(code);
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => reject(new Error("Sign-in dialog was closed.")));
      });
    });
  }

  private openPopupAndGetCode(url: string, expectedState: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const popup = window.open(url, "auth", "width=500,height=600");
      if (!popup) { reject(new Error("Failed to open sign-in popup.")); return; }

      const cleanup = () => {
        window.removeEventListener("message", onMessage);
        clearInterval(closedCheck);
      };

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== "idpResponse") return;
        cleanup();
        popup.close();
        const params = new URLSearchParams(event.data.query);
        const state = params.get("state");
        const code = params.get("code");
        if (state !== expectedState) { reject(new Error("Invalid auth state.")); return; }
        if (!code) { reject(new Error("No auth code received.")); return; }
        resolve(code);
      };

      window.addEventListener("message", onMessage);

      const closedCheck = setInterval(() => {
        if (popup.closed) { cleanup(); reject(new Error("Sign-in popup was closed.")); }
      }, 500);
    });
  }

  // ─── PKCE ─────────────────────────────────────────────────────────────────

  private generateCodeVerifier(): string {
    const a = new Uint8Array(32);
    crypto.getRandomValues(a);
    return btoa(String.fromCharCode(...a)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
    return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }
}
